import { useEffect, useMemo, useState } from "react";
import { useAppData } from "../state/AppData";
import { navigate } from "../lib/router";
import { colorFor } from '../lib/definitions/palette';
import { freshnessOf, FRESHNESS_LABEL, FRESHNESS_ORDER, type NodeFreshness } from '../lib/definitions/health';
import { NO_CONNECTIONS_EMPTY_STATE, PAGE_SIZE } from '../lib/definitions/navigation';
import { PROTOCOL_LABEL, PROTOCOL_ORDER, roleInfo, UNKNOWN_PROTOCOL_LABEL } from '../lib/definitions/protocols';
import { trustTierInfo } from '../lib/definitions/trust';
import { resolveNodeProtocol, type ResolvedProtocol } from "../lib/nodeProtocol";
import { relativeTime } from "../lib/format";
import { hasUsablePosition } from "../lib/meshview";
import { ObserverBadge } from "../components/ObserverBadge";
import { PagingBar } from "../components/PagingBar";
import { TagFilter } from "../components/TagFilter";
import { TagChips } from "../components/NodeTags";
import { nodeMatchesTags } from "../lib/nodeTags";
import type { MeshNode } from "../api/types";

type SortKey = "name" | "protocol" | "role" | "last_heard" | "position" | "observers" | "signal";
type SortDir = "asc" | "desc";
type PositionFilter = "all" | "positioned" | "unpositioned";

// Phase 2 brief, item 2: a real, independently-reachable page (not a popup)
// for the full node roster — this is also where unpositioned nodes now live
// (phase 1 stashed them in a map-overlay dropdown; that's gone, replaced by
// the "Position" column + filter here, which the map's own overflow note
// now links into instead of rendering its own list — see LiveMap.tsx).
export function NodeListPage({ presetFilter }: { presetFilter: string | null }) {
  const { nodes, observerTrust, effectiveTheme, availableProtocols, healthEverLoaded, nodesEverLoaded, tagFilter } = useAppData();
  const [search, setSearch] = useState("");
  const [protocolFilter, setProtocolFilter] = useState<"all" | ResolvedProtocol>("all");
  const [freshnessFilter, setFreshnessFilter] = useState<"all" | NodeFreshness>("all");
  const [positionFilter, setPositionFilter] = useState<PositionFilter>(presetFilter === "unpositioned" ? "unpositioned" : "all");
  const [sortKey, setSortKey] = useState<SortKey>("last_heard");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  // Client-side paging (2026-09-20: the full 1,654-node roster was
  // rendering as one 54,887px page). Unlike Audit/Coverage, the roster
  // isn't fetched per page from the network — it's the same live,
  // WS-updated in-memory roster the map depends on (AppData's `nodes`),
  // already fully resident client-side. usePagedFetch is a cursor+network
  // hook and doesn't fit that; this reuses PagingBar (same component, same
  // Prev/Next UI as Audit/Coverage) over a plain local page index instead,
  // slicing the already-filtered-and-sorted array. See NodeListPage's
  // build report for why /api/nodes itself isn't part of this fix.
  const [page, setPage] = useState(0);

  const rows = useMemo(() => {
    const list: (MeshNode & { protocolResolved: ResolvedProtocol; freshness: NodeFreshness; positioned: boolean })[] = [];
    for (const n of nodes.values()) {
      const protocolResolved = resolveNodeProtocol(n);
      const freshness = freshnessOf(n.last_heard);
      const positioned = hasUsablePosition(n);
      list.push({ ...n, protocolResolved, freshness, positioned });
    }
    return list;
  }, [nodes]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((n) => {
      if (protocolFilter !== "all" && n.protocolResolved !== protocolFilter) return false;
      if (freshnessFilter !== "all" && n.freshness !== freshnessFilter) return false;
      if (positionFilter === "positioned" && !n.positioned) return false;
      if (positionFilter === "unpositioned" && n.positioned) return false;
      if (!nodeMatchesTags(n.tags, tagFilter)) return false;
      if (q) {
        const name = (n.long_name || n.short_name || String(n.id)).toLowerCase();
        if (!name.includes(q) && !String(n.id).toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, protocolFilter, freshnessFilter, positionFilter, tagFilter]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const withKey = (n: (typeof filtered)[number]) => {
      switch (sortKey) {
        case "name":
          return (n.long_name || n.short_name || String(n.id)).toLowerCase();
        case "protocol":
          return n.protocolResolved;
        case "role":
          return n.role ?? "";
        case "last_heard":
          return n.last_heard ? Date.parse(n.last_heard) || 0 : 0;
        case "position":
          return n.positioned ? 1 : 0;
        case "observers":
          return n.observer_ids?.length ?? 0;
        case "signal":
          return n.snr ?? -999;
        default:
          return 0;
      }
    };
    return [...filtered].sort((a, b) => {
      const ka = withKey(a);
      const kb = withKey(b);
      if (ka < kb) return -1 * dir;
      if (ka > kb) return 1 * dir;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  // Reset to page 1 whenever the filtered/sorted set would change under the
  // reader — same "changing filters starts over at page 1" rule
  // usePagedFetch applies for Audit/Coverage.
  useEffect(() => {
    setPage(0);
  }, [search, protocolFilter, freshnessFilter, positionFilter, tagFilter, sortKey, sortDir]);

  const pageRows = useMemo(() => sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [sorted, page]);
  const rangeStart = pageRows.length === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = rangeStart === 0 ? 0 : rangeStart + pageRows.length - 1;
  const hasPrev = page > 0;
  const hasNext = (page + 1) * PAGE_SIZE < sorted.length;

  function onSort(key: SortKey) {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(key === "last_heard" ? "desc" : "asc");
    }
  }

  function sortIndicator(key: SortKey) {
    if (key !== sortKey) return "";
    return sortDir === "asc" ? " ▲" : " ▼";
  }

  return (
    <div className="node-list-page">
      <div className="node-list-toolbar">
        <input
          className="node-list-search"
          placeholder="Search by name or id…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="theme-toggle" role="group" aria-label="Protocol">
          <button data-active={protocolFilter === "all"} onClick={() => setProtocolFilter("all")}>
            All protocols
          </button>
          {PROTOCOL_ORDER.map((p) => (
            <button key={p} data-active={protocolFilter === p} onClick={() => setProtocolFilter(p)}>
              {PROTOCOL_LABEL[p]}
            </button>
          ))}
          <button data-active={protocolFilter === "unknown"} onClick={() => setProtocolFilter("unknown")}>
            {UNKNOWN_PROTOCOL_LABEL}
          </button>
        </div>
        <div className="theme-toggle" role="group" aria-label="Freshness">
          <button data-active={freshnessFilter === "all"} onClick={() => setFreshnessFilter("all")}>
            Any state
          </button>
          {FRESHNESS_ORDER.map((f) => (
            <button key={f} data-active={freshnessFilter === f} onClick={() => setFreshnessFilter(f)}>
              {FRESHNESS_LABEL[f]}
            </button>
          ))}
        </div>
        <div className="theme-toggle" role="group" aria-label="Position">
          <button data-active={positionFilter === "all"} onClick={() => setPositionFilter("all")}>
            Any position
          </button>
          <button data-active={positionFilter === "positioned"} onClick={() => setPositionFilter("positioned")}>
            Positioned
          </button>
          <button data-active={positionFilter === "unpositioned"} onClick={() => setPositionFilter("unpositioned")}>
            Unpositioned
          </button>
        </div>
        <TagFilter />
        <span className="node-list-count">
          {sorted.length} of {rows.length} node(s)
        </span>
      </div>

      <div className="node-list-table-wrap">
        <table className="node-list-table">
          <thead>
            <tr>
              <th onClick={() => onSort("name")}>Name{sortIndicator("name")}</th>
              <th onClick={() => onSort("protocol")}>Protocol{sortIndicator("protocol")}</th>
              <th onClick={() => onSort("role")}>Role{sortIndicator("role")}</th>
              <th onClick={() => onSort("last_heard")}>Last heard{sortIndicator("last_heard")}</th>
              <th onClick={() => onSort("position")}>Position{sortIndicator("position")}</th>
              <th onClick={() => onSort("observers")}>Observers{sortIndicator("observers")}</th>
              <th onClick={() => onSort("signal")}>Signal{sortIndicator("signal")}</th>
              <th>Tags</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((n) => {
              const role = n.protocolResolved === "unknown" ? null : roleInfo(n.protocolResolved, n.role);
              return (
                <tr key={`${n.protocolResolved}-${n.id}`} className="node-list-row" onClick={() => navigate(`#/nodes/${encodeURIComponent(String(n.id))}`)}>
                  <td>
                    {n.long_name || n.short_name || String(n.id)}
                    {n.tags && n.tags.length > 0 && (
                      <div className="node-name-tags">
                        <TagChips tags={n.tags} />
                      </div>
                    )}
                  </td>
                  <td>{n.protocolResolved === "unknown" ? UNKNOWN_PROTOCOL_LABEL : PROTOCOL_LABEL[n.protocolResolved]}</td>
                  <td>
                    {role ? (
                      <span className="badge" style={{ background: colorFor(role.color, "light") }}>
                        {role.label}
                      </span>
                    ) : (
                      "--"
                    )}
                  </td>
                  <td>
                    <span className={`freshness-dot freshness-${n.freshness}`} /> {relativeTime(n.last_heard)}
                  </td>
                  <td>{n.positioned ? "Positioned" : "No position"}</td>
                  <td>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {n.observer_ids && n.observer_ids.length > 0
                        ? n.observer_ids.map((oid) => <ObserverBadge key={oid} observerId={oid} trustTier={observerTrust.get(oid)} compact />)
                        : "--"}
                    </div>
                  </td>
                  <td>{n.snr != null ? `${n.snr}dB` : "--"}</td>
                  <td>
                    <TagChips tags={n.tags} />
                  </td>
                </tr>
              );
            })}
            {/* 2026-09-30 data-layer fix: pageRows being empty used to mean
                only one of two things (no connection configured, or filters
                exclude everything) and picked between them using
                availableProtocols.length===0 alone — but that reads empty
                on first render too, before /api/health has ever answered,
                which isn't "no connections" at all. Now gated on the real
                query states: while the node roster itself hasn't loaded yet
                (!nodesEverLoaded — a truly first-ever visit, empty
                persisted cache), this renders a neutral loading row, never
                an empty-state message. Once nodes HAVE loaded and the table
                is genuinely empty, NO_CONNECTIONS_EMPTY_STATE is only shown
                once health has also actually answered (healthEverLoaded). */}
            {pageRows.length === 0 &&
              (!nodesEverLoaded ? (
                <tr>
                  <td colSpan={8} className="empty-state">
                    Loading…
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={8} className="empty-state">
                    {healthEverLoaded && availableProtocols.length === 0 ? NO_CONNECTIONS_EMPTY_STATE : "No nodes match the current filters."}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {pageRows.length > 0 && (
        <PagingBar
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          total={sorted.length}
          hasPrev={hasPrev}
          hasNext={hasNext}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => p + 1)}
        />
      )}
    </div>
  );
}
